'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import ScoreGauge from '@/components/tools/ScoreGauge';
import TagCloud from '@/components/tools/TagCloud';
import ProgressBar from '@/components/tools/ProgressBar';
import CopyButton from '@/components/tools/CopyButton';
import {
  keywordDictionary,
  type CategoryKey,
} from '@/lib/tool-data/keyword-dictionary';
import { atsCheckWithAI, type AIATSResult } from '@/lib/tools/api';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ScanResult {
  overallScore: number;
  matchedKeywords: string[];
  missingKeywords: string[];
  hardSkillsScore: number;
  softSkillsScore: number;
  jobTitleScore: number;
  experienceLevelScore: number;
  tips: string[];
  jdKeywordCount: number;
  resumeWordCount: number;
}

/* ------------------------------------------------------------------ */
/*  Normalization helpers                                               */
/* ------------------------------------------------------------------ */

/** Normalize text: lowercase, collapse whitespace */
function normalize(text: string): string {
  return text.toLowerCase().replace(/[\s\u00A0]+/g, ' ').trim();
}

/** Strip common plural/tense suffixes for fuzzy matching */
function stem(word: string): string {
  const w = word.toLowerCase();
  if (w.endsWith('ies') && w.length > 4) return w.slice(0, -3) + 'y';
  if (w.endsWith('ses') && w.length > 4) return w.slice(0, -2);
  if (w.endsWith('ing') && w.length > 5) return w.slice(0, -3);
  if (w.endsWith('ed') && w.length > 4) return w.slice(0, -2);
  if (w.endsWith('s') && !w.endsWith('ss') && w.length > 3) return w.slice(0, -1);
  return w;
}

/** Check if a term (possibly multi-word) appears in the text, with plural/case-insensitive matching */
function termAppearsIn(term: string, normalizedText: string): boolean {
  const escaped = term
    .toLowerCase()
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Try exact word-boundary match first
  const exact = new RegExp(`\\b${escaped}\\b`, 'i');
  if (exact.test(normalizedText)) return true;
  // Try stemmed match for single-word terms
  if (!term.includes(' ')) {
    const stemmed = stem(term);
    const stemRegex = new RegExp(`\\b${stemmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
    if (stemRegex.test(normalizedText)) return true;
  }
  return false;
}

/* ------------------------------------------------------------------ */
/*  Keyword extraction from job description                            */
/* ------------------------------------------------------------------ */

/** Match job description text against the keyword dictionary, returning matched category results */
function extractDictionaryKeywords(
  text: string,
  categories: CategoryKey[],
): { keyword: string; category: CategoryKey }[] {
  const normalizedText = normalize(text);
  const results: { keyword: string; category: CategoryKey }[] = [];
  const seen = new Set<string>();

  for (const catKey of categories) {
    const category = keywordDictionary[catKey];
    for (const entry of category.keywords) {
      if (seen.has(entry.keyword.toLowerCase())) continue;
      const termsToCheck = [entry.keyword, ...(entry.aliases || [])];
      for (const term of termsToCheck) {
        if (termAppearsIn(term, normalizedText)) {
          seen.add(entry.keyword.toLowerCase());
          results.push({ keyword: entry.keyword, category: catKey });
          break;
        }
      }
    }
  }

  return results;
}

/* ------------------------------------------------------------------ */
/*  Experience level detection                                         */
/* ------------------------------------------------------------------ */

const experiencePatterns = {
  entry: [
    /\bentry[\s-]?level\b/i,
    /\b0[\s-]?[–-][\s-]?2\s*years?\b/i,
    /\bjunior\b/i,
    /\bgraduate\b/i,
    /\bintern\b/i,
    /\bnew\s+grad\b/i,
  ],
  mid: [
    /\bmid[\s-]?level\b/i,
    /\b[2-5]\+?\s*years?\b/i,
    /\b3[\s-]?[–-][\s-]?5\s*years?\b/i,
    /\bintermediate\b/i,
  ],
  senior: [
    /\bsenior\b/i,
    /\b[5-9]\+?\s*years?\b/i,
    /\b5[\s-]?[–-][\s-]?10\s*years?\b/i,
    /\blead\b/i,
    /\bstaff\b/i,
    /\bprincipal\b/i,
  ],
  executive: [
    /\bexecutive\b/i,
    /\bdirector\b/i,
    /\bvice\s+president\b/i,
    /\bvp\b/i,
    /\bc[\s-]?suite\b/i,
    /\bchief\b/i,
    /\b10\+?\s*years?\b/i,
    /\bhead\s+of\b/i,
  ],
};

function detectExperienceLevel(text: string): string | null {
  for (const [level, patterns] of Object.entries(experiencePatterns)) {
    for (const pattern of patterns) {
      if (pattern.test(text)) return level;
    }
  }
  return null;
}

/* ------------------------------------------------------------------ */
/*  Job title extraction                                               */
/* ------------------------------------------------------------------ */

/** Extract the likely job title from the first few lines of a job description */
function extractJobTitle(jdText: string): string[] {
  const lines = jdText.split(/\n/).slice(0, 5);
  const titleWords: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    // Heuristic: short lines at the top are often the title
    if (trimmed.length > 3 && trimmed.length < 100) {
      const words = trimmed
        .replace(/[^a-zA-Z\s/-]/g, ' ')
        .split(/\s+/)
        .filter((w) => w.length > 1);
      titleWords.push(...words.map((w) => w.toLowerCase()));
      if (titleWords.length >= 6) break;
    }
  }

  return [...new Set(titleWords)];
}

/* ------------------------------------------------------------------ */
/*  Main scanning algorithm                                            */
/* ------------------------------------------------------------------ */

function scanResume(resumeText: string, jdText: string): ScanResult {
  const normalizedResume = normalize(resumeText);
  const normalizedJD = normalize(jdText);

  // 1. Extract dictionary-matched keywords from JD
  const allCategories: CategoryKey[] = [
    'hard_skills',
    'soft_skills',
    'certifications',
    'tools_technologies',
    'industry_terms',
  ];

  const jdDictionaryKeywords = extractDictionaryKeywords(jdText, allCategories);

  // 2. Common stop words to filter out (used for raw keyword extraction below)
  const stopWords = new Set([
    'the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'had', 'her',
    'was', 'one', 'our', 'out', 'has', 'have', 'been', 'will', 'with', 'this',
    'that', 'from', 'they', 'were', 'which', 'their', 'what', 'about', 'would',
    'make', 'like', 'just', 'over', 'such', 'than', 'them', 'some', 'also',
    'into', 'other', 'could', 'more', 'very', 'when', 'come', 'your', 'how',
    'its', 'may', 'must', 'should', 'each', 'any', 'these', 'who', 'where',
    'does', 'did', 'doing', 'being', 'here', 'there', 'then', 'only',
    'both', 'through', 'during', 'before', 'after', 'above', 'between',
    'those', 'own', 'same', 'while', 'under', 'including', 'within',
    'able', 'work', 'working', 'role', 'join', 'team', 'company', 'job',
    'position', 'looking', 'ideal', 'candidate', 'apply', 'application',
    'please', 'description', 'requirements', 'required', 'preferred',
    'experience', 'years', 'strong', 'proven', 'excellent', 'ability',
    'skills', 'knowledge', 'understanding', 'responsible', 'responsibilities',
    'opportunity', 'benefits', 'salary', 'bonus', 'equal', 'employer',
    'we', 'us', 'or', 'an', 'as', 'at', 'be', 'by', 'do', 'if', 'in',
    'is', 'it', 'no', 'of', 'on', 'so', 'to', 'up',
  ]);

  // 3. Check dictionary keywords against resume
  const matchedKeywords: string[] = [];
  const missingKeywords: string[] = [];
  const hardSkillsMatched: string[] = [];
  const hardSkillsTotal: string[] = [];
  const softSkillsMatched: string[] = [];
  const softSkillsTotal: string[] = [];

  for (const { keyword, category } of jdDictionaryKeywords) {
    const entry = keywordDictionary[category].keywords.find(
      (k) => k.keyword === keyword,
    );
    const aliases = entry?.aliases || [];
    const allTerms = [keyword, ...aliases];
    const found = allTerms.some((t) => termAppearsIn(t, normalizedResume));

    if (found) {
      matchedKeywords.push(keyword);
    } else {
      missingKeywords.push(keyword);
    }

    // Track category-specific matches
    if (category === 'hard_skills' || category === 'tools_technologies' || category === 'certifications') {
      hardSkillsTotal.push(keyword);
      if (found) hardSkillsMatched.push(keyword);
    }
    if (category === 'soft_skills') {
      softSkillsTotal.push(keyword);
      if (found) softSkillsMatched.push(keyword);
    }
  }

  // 4. Also find important raw keywords from the JD that aren't in the dictionary
  //    (multi-word phrases from the JD that appear frequently)
  const jdWords = normalizedJD
    .replace(/[^a-z0-9#+.\-/& ]/gi, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 2 && !stopWords.has(w));

  // Count word frequency in JD
  const wordFreq = new Map<string, number>();
  for (const w of jdWords) {
    wordFreq.set(w, (wordFreq.get(w) || 0) + 1);
  }

  // Get high-frequency JD words (appear 2+ times) not already captured
  const alreadyCaptured = new Set([
    ...matchedKeywords.map((k) => k.toLowerCase()),
    ...missingKeywords.map((k) => k.toLowerCase()),
  ]);

  const additionalJDKeywords: string[] = [];
  for (const [word, count] of wordFreq.entries()) {
    if (count >= 2 && !alreadyCaptured.has(word) && !stopWords.has(word) && word.length > 3) {
      additionalJDKeywords.push(word);
    }
  }

  // Check additional keywords in resume
  for (const word of additionalJDKeywords.slice(0, 15)) {
    if (termAppearsIn(word, normalizedResume)) {
      if (!matchedKeywords.some((k) => k.toLowerCase() === word)) {
        matchedKeywords.push(word);
      }
    } else {
      if (!missingKeywords.some((k) => k.toLowerCase() === word)) {
        missingKeywords.push(word);
      }
    }
  }

  // 5. Job title matching
  const titleWords = extractJobTitle(jdText);
  const titleMatches = titleWords.filter((w) =>
    termAppearsIn(w, normalizedResume),
  );
  const jobTitleScore =
    titleWords.length > 0
      ? Math.round((titleMatches.length / titleWords.length) * 100)
      : 50; // default when we can't detect title

  // 6. Experience level matching
  const jdLevel = detectExperienceLevel(jdText);
  const resumeLevel = detectExperienceLevel(resumeText);
  let experienceLevelScore = 50; // default
  if (jdLevel && resumeLevel) {
    if (jdLevel === resumeLevel) {
      experienceLevelScore = 100;
    } else {
      const levels = ['entry', 'mid', 'senior', 'executive'];
      const jdIdx = levels.indexOf(jdLevel);
      const resumeIdx = levels.indexOf(resumeLevel);
      const diff = Math.abs(jdIdx - resumeIdx);
      experienceLevelScore = diff === 1 ? 65 : 30;
    }
  } else if (jdLevel || resumeLevel) {
    experienceLevelScore = 40;
  }

  // 7. Calculate section scores
  const hardSkillsScore =
    hardSkillsTotal.length > 0
      ? Math.round((hardSkillsMatched.length / hardSkillsTotal.length) * 100)
      : 50;

  const softSkillsScore =
    softSkillsTotal.length > 0
      ? Math.round((softSkillsMatched.length / softSkillsTotal.length) * 100)
      : 50;

  // 8. Overall score calculation (weighted)
  const totalJDKeywords = matchedKeywords.length + missingKeywords.length;
  const keywordMatchRate =
    totalJDKeywords > 0 ? matchedKeywords.length / totalJDKeywords : 0;

  const overallScore = Math.round(
    keywordMatchRate * 55 +     // keyword match is biggest factor
    (hardSkillsScore / 100) * 20 +   // hard skills
    (softSkillsScore / 100) * 5 +    // soft skills
    (jobTitleScore / 100) * 10 +     // job title match
    (experienceLevelScore / 100) * 10 // experience level
  );

  // 9. Generate actionable tips
  const tips: string[] = [];

  if (overallScore >= 80) {
    tips.push(
      'Your resume is a strong match for this job description. Focus on fine-tuning the details.',
    );
  } else if (overallScore >= 60) {
    tips.push(
      'Your resume is a decent match, but there are gaps. Adding the missing keywords could significantly improve your chances.',
    );
  } else {
    tips.push(
      'Your resume has significant gaps compared to this job description. Consider tailoring it more closely before applying.',
    );
  }

  if (missingKeywords.length > 0) {
    const topMissing = missingKeywords.slice(0, 5).join(', ');
    tips.push(
      `Add these missing keywords to your resume where they truthfully apply: ${topMissing}.`,
    );
  }

  if (hardSkillsScore < 50) {
    tips.push(
      'Your resume is missing many of the hard skills and tools listed in the job description. Add a dedicated "Skills" section if you do not have one.',
    );
  }

  if (softSkillsScore < 40) {
    tips.push(
      'Consider weaving soft skills into your bullet points. Instead of listing "leadership," show it through an achievement like "Led a team of 8 engineers."',
    );
  }

  if (jobTitleScore < 50) {
    tips.push(
      'Your resume may not clearly reflect the target job title. Consider using the exact title from the posting in your resume headline or summary.',
    );
  }

  if (experienceLevelScore < 50) {
    tips.push(
      'The experience level in your resume may not align with the job requirements. Make sure your years of experience and role seniority are clearly stated.',
    );
  }

  const resumeWordCount = normalizedResume.split(/\s+/).filter(Boolean).length;
  if (resumeWordCount < 150) {
    tips.push(
      'Your resume text seems short. Make sure you pasted the full content including all roles, skills, and education.',
    );
  }

  tips.push(
    'Use the exact phrasing from the job description where possible. ATS systems often do literal string matching.',
  );

  return {
    overallScore: Math.min(100, Math.max(0, overallScore)),
    matchedKeywords,
    missingKeywords,
    hardSkillsScore,
    softSkillsScore,
    jobTitleScore,
    experienceLevelScore,
    tips,
    jdKeywordCount: totalJDKeywords,
    resumeWordCount,
  };
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ATSScannerContent() {
  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [result, setResult] = useState<ScanResult | null>(null);
  const [hasScanned, setHasScanned] = useState(false);
  const [aiResult, setAiResult] = useState<AIATSResult | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const handleScan = () => {
    if (!resumeText.trim() || !jobDescription.trim()) return;
    const scanResult = scanResume(resumeText, jobDescription);
    setResult(scanResult);
    setHasScanned(true);
  };

  const handleReset = () => {
    setResumeText('');
    setJobDescription('');
    setResult(null);
    setHasScanned(false);
    setAiResult(null);
    setAiError(null);
  };

  async function handleAIScan() {
    if (!resumeText.trim() || !jobDescription.trim()) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const result = await atsCheckWithAI(resumeText, jobDescription);
      setAiResult(result);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'AI analysis failed. Please try again.');
    } finally {
      setAiLoading(false);
    }
  }

  const missingKeywordsText = result
    ? result.missingKeywords.join(', ')
    : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="ATS Resume Scanner"
        description="Check how well your resume matches a job description. Get an ATS compatibility score, find missing keywords, and get actionable tips to beat applicant tracking systems."
        category="Resume Tool"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Paste Your Resume & Job Description
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Resume textarea */}
            <div>
              <label
                htmlFor="resume-text"
                className="block text-sm font-semibold text-gray-700 mb-1.5"
              >
                Your Resume Text
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Copy and paste your entire resume content.
              </p>
              <textarea
                id="resume-text"
                rows={12}
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
                placeholder="Paste your full resume text here..."
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y text-sm leading-relaxed"
              />
            </div>

            {/* Job description textarea */}
            <div>
              <label
                htmlFor="job-description"
                className="block text-sm font-semibold text-gray-700 mb-1.5"
              >
                Job Description
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Copy and paste the full job listing.
              </p>
              <textarea
                id="job-description"
                rows={12}
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste the full job description here..."
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y text-sm leading-relaxed"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleScan}
              disabled={!resumeText.trim() || !jobDescription.trim()}
              className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold py-3 px-8 rounded-xl shadow text-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Scan Resume
            </button>
            {hasScanned && (
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Start Over
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        {hasScanned && result && (
          <div className="space-y-8">
            {/* Overall Score Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex flex-col items-center">
                <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-4">
                  ATS Compatibility Score
                </p>
                <ScoreGauge
                  score={result.overallScore}
                  label="ATS Match"
                  size={220}
                />
                <p className="text-gray-600 mt-4 text-center max-w-md">
                  {result.overallScore >= 80
                    ? 'Excellent match! Your resume aligns well with this job description.'
                    : result.overallScore >= 60
                      ? 'Good match with room for improvement. Add the missing keywords to boost your score.'
                      : result.overallScore >= 40
                        ? 'Moderate match. Your resume needs more tailoring to this specific job.'
                        : 'Low match. Significant tailoring is needed to pass ATS filters for this job.'}
                </p>
                <div className="flex flex-wrap justify-center gap-4 mt-4 text-sm text-gray-500">
                  <span>
                    <strong className="text-gray-900">{result.matchedKeywords.length}</strong>{' '}
                    keywords matched
                  </span>
                  <span>
                    <strong className="text-gray-900">{result.missingKeywords.length}</strong>{' '}
                    keywords missing
                  </span>
                  <span>
                    <strong className="text-gray-900">{result.jdKeywordCount}</strong>{' '}
                    total keywords found
                  </span>
                </div>
              </div>
            </div>

            {/* Section Scores */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Section Breakdown
              </h3>
              <div className="space-y-5">
                <ProgressBar
                  value={result.hardSkillsScore}
                  label="Hard Skills & Tools Match"
                />
                <ProgressBar
                  value={result.softSkillsScore}
                  label="Soft Skills Match"
                />
                <ProgressBar
                  value={result.jobTitleScore}
                  label="Job Title Match"
                />
                <ProgressBar
                  value={result.experienceLevelScore}
                  label="Experience Level Match"
                />
              </div>
            </div>

            {/* Matched Keywords */}
            {result.matchedKeywords.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Matched Keywords ({result.matchedKeywords.length})
                  </h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  These keywords from the job description were found in your
                  resume. Good job including them.
                </p>
                <TagCloud
                  tags={result.matchedKeywords.map((k) => ({ text: k }))}
                  variant="green"
                />
              </div>
            )}

            {/* Missing Keywords */}
            {result.missingKeywords.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Missing Keywords ({result.missingKeywords.length})
                  </h3>
                  <CopyButton
                    text={missingKeywordsText}
                    label="Copy Missing"
                  />
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  These keywords appear in the job description but not in your
                  resume. Add the ones that truthfully apply to your experience.
                </p>
                <TagCloud
                  tags={result.missingKeywords.map((k) => ({ text: k }))}
                  variant="red"
                />
              </div>
            )}

            {/* Actionable Tips */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                Tips to Improve Your Score
              </h3>
              <ul className="space-y-3">
                {result.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1 flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </span>
                    <span className="text-blue-800 leading-relaxed">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* AI-Powered Deep Analysis */}
            {!aiResult && !aiLoading && (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-2xl p-8 text-center">
                <h3 className="text-xl font-bold text-purple-900 mb-2">
                  Want AI-powered analysis?
                </h3>
                <p className="text-sm text-purple-700 mb-4">
                  Our AI reads your full resume and job description to find context-aware matches, synonyms, and deeper insights that keyword matching alone can miss.
                </p>
                <button
                  onClick={handleAIScan}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-3 px-8 rounded-xl shadow-lg transition-all duration-200 text-lg"
                >
                  Enhance with AI Analysis
                </button>
              </div>
            )}

            {aiLoading && (
              <div className="bg-white rounded-2xl shadow-lg border border-purple-200 p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-purple-200 border-t-purple-600 mb-4" />
                <p className="text-gray-600 font-medium">AI is comparing your resume to the job description...</p>
                <p className="text-sm text-gray-400 mt-1">This usually takes 5-10 seconds</p>
              </div>
            )}

            {aiError && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
                <p className="text-red-700 font-medium mb-3">{aiError}</p>
                <button
                  onClick={handleAIScan}
                  className="text-red-700 underline hover:text-red-800 text-sm font-medium"
                >
                  Try again
                </button>
              </div>
            )}

            {aiResult && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    AI-POWERED
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Deep Analysis</h2>
                </div>

                {/* AI Score */}
                <div className="bg-white rounded-2xl shadow-lg border border-purple-200 p-8">
                  <div className="flex flex-col items-center">
                    <p className="text-sm font-semibold text-purple-600 uppercase tracking-wide mb-4">
                      AI Match Score
                    </p>
                    <ScoreGauge
                      score={aiResult.score}
                      label="AI Score"
                      size={180}
                    />
                    <p className="text-gray-600 mt-3 text-center max-w-md text-sm">
                      The AI score considers context, synonyms, and semantic relevance beyond simple keyword matching.
                    </p>
                  </div>
                </div>

                {/* Category Breakdown */}
                {aiResult.categoryBreakdown.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">
                      AI Category Breakdown
                    </h3>
                    <div className="space-y-4">
                      {aiResult.categoryBreakdown.map((cat, i) => (
                        <div key={i}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-gray-700">{cat.category}</span>
                            <span className="text-gray-500">{cat.matched}/{cat.total} matched</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2.5">
                            <div
                              className={`h-2.5 rounded-full transition-all duration-500 ${
                                cat.total > 0 && cat.matched / cat.total >= 0.7
                                  ? 'bg-green-500'
                                  : cat.total > 0 && cat.matched / cat.total >= 0.4
                                    ? 'bg-amber-500'
                                    : 'bg-red-500'
                              }`}
                              style={{ width: `${cat.total > 0 ? (cat.matched / cat.total) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Matched Keywords with Context */}
                {aiResult.matchedKeywords.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-lg border border-green-100 p-8">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-4">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      AI-Matched Keywords ({aiResult.matchedKeywords.length})
                    </h3>
                    <div className="space-y-3">
                      {aiResult.matchedKeywords.map((kw, i) => (
                        <div key={i} className="flex items-start gap-3 bg-green-50 border border-green-100 rounded-xl px-4 py-3">
                          <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full mt-0.5 flex-shrink-0">
                            {kw.keyword}
                          </span>
                          <span className="text-sm text-gray-600">{kw.context}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Missing Keywords */}
                {aiResult.missingKeywords.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-lg border border-red-100 p-8">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-4">
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      AI-Identified Missing Keywords ({aiResult.missingKeywords.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {aiResult.missingKeywords.map((kw) => (
                        <span key={kw} className="inline-block bg-red-50 text-red-700 border border-red-200 px-3 py-1 rounded-full text-sm font-medium">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Suggestions */}
                {aiResult.suggestions.length > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-2xl p-8">
                    <h3 className="text-xl font-bold text-purple-900 mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      AI Suggestions to Improve Your Score
                    </h3>
                    <ul className="space-y-3">
                      {aiResult.suggestions.map((suggestion, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="mt-1 flex-shrink-0 w-5 h-5 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold">
                            {i + 1}
                          </span>
                          <span className="text-purple-800 leading-relaxed">{suggestion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Educational Section */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                What Is an ATS and Why Does It Matter?
              </h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  An <strong>Applicant Tracking System (ATS)</strong> is software
                  that companies use to manage job applications. Over 97% of
                  Fortune 500 companies and 75% of all employers use some form of
                  ATS to screen resumes before a human ever sees them.
                </p>
                <p>
                  The ATS scans your resume for relevant keywords, skills, and
                  qualifications that match the job description. If your resume
                  does not contain enough matching terms, it may be filtered out
                  automatically -- even if you are qualified for the role.
                </p>

                <h4 className="text-lg font-bold text-gray-900 pt-2">
                  How ATS Scoring Works
                </h4>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <strong>Keyword matching:</strong> The system looks for
                    specific skills, tools, certifications, and phrases from the
                    job description in your resume.
                  </li>
                  <li>
                    <strong>Exact phrasing matters:</strong> If the job says
                    &quot;project management&quot; and your resume says &quot;managed
                    projects,&quot; some ATS systems will not count it as a match.
                  </li>
                  <li>
                    <strong>Section headers:</strong> Use standard headings like
                    &quot;Work Experience,&quot; &quot;Education,&quot; and
                    &quot;Skills&quot; so the ATS can parse your resume correctly.
                  </li>
                  <li>
                    <strong>File format:</strong> Most ATS systems prefer
                    .docx or .pdf files. Avoid tables, images, and complex
                    formatting that can confuse the parser.
                  </li>
                </ul>

                <h4 className="text-lg font-bold text-gray-900 pt-2">
                  Tips for Beating ATS Filters
                </h4>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    Tailor your resume to each job description -- use the same
                    language the employer uses.
                  </li>
                  <li>
                    Include both spelled-out terms and acronyms (e.g.,
                    &quot;Search Engine Optimization (SEO)&quot;).
                  </li>
                  <li>
                    Put keywords in context within your bullet points rather than
                    stuffing them into a hidden section.
                  </li>
                  <li>
                    Do not try to trick the system with white text or keyword
                    stuffing -- modern ATS systems detect this.
                  </li>
                  <li>
                    Keep formatting simple: no headers/footers, no text boxes, and
                    use standard fonts.
                  </li>
                </ul>
                <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  <strong>Sources &amp; Further Reading:</strong>{' '}
                  <a href="https://www.indeed.com/career-advice/resumes-cover-letters/what-is-an-ats-resume" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: What Is an ATS Resume?</a>
                  {' · '}
                  <a href="https://www.careeronestop.org/JobSearch/Resumes/ResumeGuide/TopResumeTips/tips-for-writing-your-resume.aspx" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop Resume Tips (U.S. DOL)</a>
                  {' · '}
                  <a href="https://hbr.org/2021/05/a-guide-to-getting-past-the-bots-that-read-resumes" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">HBR: Getting Past the Bots That Read Resumes</a>
                </p>
              </div>
            </div>

            {/* CTA */}
            <ToolCTA
              headline="Want a resume that passes every ATS?"
              description="Superpower Resume automatically tailors your resume to each job description, weaving in the right keywords so you pass ATS filters and land more interviews."
              buttonText="Create an ATS-Optimized Resume"
              buttonHref="https://app.superpowerresume.com/sign-up"
            />
          </div>
        )}

        {/* Show CTA even before scanning */}
        {!hasScanned && (
          <ToolCTA
            headline="Skip the manual work -- let AI optimize your resume"
            description="Superpower Resume analyzes job descriptions and automatically tailors your resume with the right keywords, formatting, and phrasing to pass any ATS."
            buttonText="Create an ATS-Optimized Resume"
            buttonHref="https://app.superpowerresume.com/sign-up"
          />
        )}
      </div>
    </div>
  );
}
